import classNames from "classnames";
import React, {
  FC,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { IconType } from "react-icons";
import {
  LuAlertCircle,
  LuArrowUp,
  LuArrowUpRight,
  LuBriefcase,
  LuBuilding2,
  LuCheck,
  LuChevronRight,
  LuMic,
  LuReceipt,
  LuRotateCcw,
  LuSparkles,
  LuSquare,
  LuUser,
  LuX,
} from "react-icons/lu";
import { useNavigate } from "react-router-dom";

import { useAppDispatch } from "../../../redux/redux";
import {
  AiAskItemKind,
  aiSlice,
  useAiAskMutation,
} from "../../../slices/AiSlice";
import {
  appendDictation,
  useDictation,
} from "../../work/pages/details/widgets/work-notes/useDictation";
import { aiErrorMessage } from "../ai.utils";
import { HelpReplyText } from "../help/HelpReplyText";
import { Spinner } from "../WriteWithAi";
import {
  askHistory,
  AskItem,
  canAsk,
  MAX_ASK_CHARS,
  moreText,
  normalizeAskResponse,
} from "./ask.utils";
import { AskChart } from "./AskChart";
import {
  AskStreamFailed,
  AskStreamUnavailable,
  AskTextUpdate,
  streamAsk,
  streamingDisplayText,
} from "./askStream";
import { AskTurnState, withStep, withStreamText } from "./askTurnState";
import { AskProposal } from "./proposal.utils";
import { ProposalCard } from "./ProposalCard";
import { TextReveal } from "./textReveal";

/*
 * "Ask ProCharted" answer panel, shown under the top search box (portal,
 * fixed position, z-40 like the help chat so the app's windows stay on top).
 * Doesn't block the page: no backdrop; Esc / × close it.
 */

export type AskRequest = { id: number; question: string };

const KIND_LOOK: Record<
  AiAskItemKind,
  { icon: IconType; tint: string; label: string }
> = {
  work: {
    icon: LuBriefcase,
    tint: "bg-[#EEEAFF] text-[#6B4EFF]",
    label: "Work",
  },
  client: {
    icon: LuBuilding2,
    tint: "bg-[#E7F1FF] text-[#2F6FDE]",
    label: "Client",
  },
  invoice: {
    icon: LuReceipt,
    tint: "bg-[#E6F6EE] text-[#1E8A5A]",
    label: "Invoice",
  },
  person: {
    icon: LuUser,
    tint: "bg-[#FFF1E5] text-[#C2620E]",
    label: "Person",
  },
};

const MAX_WIDTH = 640;
const GUTTER = 16;

type Position = { top: number; left: number; width: number; maxHeight: number };

const usePanelPosition = (anchorRef: RefObject<HTMLElement>) => {
  const [position, setPosition] = useState<Position | null>(null);
  const measure = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    const viewport = window.innerWidth;
    const width = Math.min(
      Math.max(rect.width, MAX_WIDTH),
      viewport - GUTTER * 2,
    );
    const centered = rect.left + rect.width / 2 - width / 2;
    const left = Math.max(
      GUTTER,
      Math.min(centered, viewport - width - GUTTER),
    );
    const top = rect.bottom + 8;
    setPosition({
      top,
      left,
      width,
      maxHeight: Math.max(220, window.innerHeight - top - GUTTER),
    });
  }, [anchorRef]);

  useLayoutEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);
  return position;
};

type State = AskTurnState;

type Props = {
  request: AskRequest;
  orgId: string;
  anchorRef: RefObject<HTMLElement>;
  onClose: () => void;
  /** "Review & create" on a proposal: open the pre-filled window. */
  onReview?: (proposal: AskProposal) => void;
  /** Kept mounted (answer kept) but not shown while that window is open. */
  hidden?: boolean;
};

const ItemRow: FC<{ item: AskItem; onOpen: (link: string) => void }> = ({
  item,
  onOpen,
}) => {
  const look = KIND_LOOK[item.kind] || KIND_LOOK.work;
  const Icon = look.icon;
  const content = (
    <>
      <span
        aria-hidden={"true"}
        className={classNames(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px]",
          look.tint,
        )}>
        <Icon size={16} strokeWidth={2.2} />
      </span>
      <span className={"min-w-0 flex-1"}>
        <span
          className={
            "block truncate text-[13.5px] font-bold leading-[1.35] text-[#1F2433]"
          }>
          {item.title}
        </span>
        {item.subtitle && (
          <span
            className={
              "block truncate text-[12px] font-medium leading-[1.4] text-[#7A8194]"
            }>
            {item.subtitle}
          </span>
        )}
      </span>
      {item.link && (
        <LuChevronRight
          aria-hidden={"true"}
          size={16}
          className={"shrink-0 text-[#B4BAC8] group-hover:text-[#6B4EFF]"}
        />
      )}
    </>
  );
  const rowClass =
    "group flex w-full items-center gap-3 rounded-[10px] px-2.5 py-2 text-left";
  if (!item.link) {
    return <div className={rowClass}>{content}</div>;
  }
  const link = item.link;
  return (
    <button
      type={"button"}
      title={`${look.label}: ${item.title}`}
      onClick={() => onOpen(link)}
      className={classNames(
        rowClass,
        "hover:bg-[#F6F4FF] focus-visible:bg-[#F6F4FF] focus-visible:outline-none",
      )}>
      {content}
    </button>
  );
};

type AnswerProps = {
  state: State;
  /** "Stop" while thinking (Roadmap 1857). */
  onStop?: () => void;
  onOpen: (link: string) => void;
  /** This answer's proposal was cancelled ("Nothing was created"). */
  proposalDismissed?: boolean;
  onReview?: (proposal: AskProposal) => void;
  onDismissProposal?: () => void;
};

export const AnswerView: FC<AnswerProps> = ({
  state,
  onStop,
  onOpen,
  proposalDismissed = false,
  onReview,
  onDismissProposal,
}) => {
  if (state.status === "thinking" && streamingDisplayText(state.text ?? "")) {
    // The answer is being written: steps (done) above, the text with a caret.
    return (
      <div className={"space-y-2"}>
        {state.steps?.length ? (
          <ul className={"space-y-0.5"}>
            {state.steps.map((step) => (
              <li
                key={step}
                className={
                  "flex items-start gap-1.5 text-[12px] leading-[1.45] text-[#9AA1B2]"
                }>
                <LuCheck
                  aria-hidden={"true"}
                  size={12}
                  strokeWidth={3}
                  className={"mt-[2px] shrink-0"}
                />
                <span className={"min-w-0 break-words"}>{step}</span>
              </li>
            ))}
          </ul>
        ) : null}
        <div
          aria-live={"off"}
          aria-busy={"true"}
          className={
            "whitespace-pre-wrap break-words text-[14px] leading-[1.6] text-[#3A4256]"
          }>
          {streamingDisplayText(state.text ?? "")}
          <span
            aria-hidden={"true"}
            data-testid={"ask-caret"}
            className={
              "ml-0.5 inline-block h-[1.05em] w-[2px] translate-y-[3px] animate-pulse rounded-full bg-[#6B4EFF]"
            }
          />
        </div>
        {onStop && (
          <button
            type={"button"}
            onClick={onStop}
            title={"Stop this question and ask another one"}
            className={
              "inline-flex h-7 items-center gap-1.5 rounded-[9px] border border-[#DCE2EA] bg-white px-2 text-[12px] font-bold text-[#3A4256] hover:border-[#E5484D] hover:text-[#E5484D]"
            }>
            <LuSquare size={9} fill={"currentColor"} />
            Stop
          </button>
        )}
      </div>
    );
  }
  if (state.status === "thinking") {
    return (
      <div
        role={"status"}
        className={"flex items-start gap-3 rounded-[12px] bg-[#F7F6FE] p-4"}>
        <Spinner className={"mt-0.5"} />
        <div className={"min-w-0 flex-1"}>
          <div className={"text-[13.5px] font-bold text-[#1F2433]"}>
            Thinking…
          </div>
          {state.steps?.length ? (
            <ul className={"mt-1 space-y-0.5"} aria-live={"polite"}>
              {state.steps.map((step, index) => {
                const current = index === (state.steps?.length ?? 0) - 1;
                return (
                  <li
                    key={step}
                    className={classNames(
                      "flex items-start gap-1.5 text-[12.5px] leading-[1.45]",
                      current
                        ? "font-semibold text-[#3A4256]"
                        : "text-[#9AA1B2]",
                    )}>
                    {current ? (
                      <span
                        aria-hidden={"true"}
                        className={
                          "mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#6B4EFF]"
                        }
                      />
                    ) : (
                      <LuCheck
                        aria-hidden={"true"}
                        size={12}
                        strokeWidth={3}
                        className={"mt-[3px] shrink-0"}
                      />
                    )}
                    <span className={"min-w-0 break-words"}>{step}</span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className={"mt-0.5 text-[12.5px] text-[#7A8194]"}>
              Looking through your firm&apos;s data. This can take a few
              seconds.
            </div>
          )}
        </div>
        {onStop && (
          <button
            type={"button"}
            onClick={onStop}
            title={"Stop this question and ask another one"}
            className={
              "inline-flex h-8 shrink-0 items-center gap-1.5 rounded-[10px] border border-[#DCE2EA] bg-white px-2.5 text-[12.5px] font-bold text-[#3A4256] hover:border-[#E5484D] hover:text-[#E5484D]"
            }>
            <LuSquare size={10} fill={"currentColor"} />
            Stop
          </button>
        )}
      </div>
    );
  }
  if (state.status === "stopped") {
    return (
      <div className={"text-[13px] font-semibold text-[#7A8194]"}>
        Stopped. Ask another question below.
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div
        role={"alert"}
        className={
          "flex items-start gap-2.5 rounded-[12px] border border-[#F6D2D2] bg-[#FFF5F5] p-3.5 text-[13px] font-medium leading-[1.5] text-[#B42318]"
        }>
        <LuAlertCircle size={17} className={"mt-[1px] shrink-0"} />
        <span>{state.message}</span>
      </div>
    );
  }
  const { answer } = state;
  return (
    <div className={"space-y-3"}>
      {answer.answer ? (
        <div className={"text-[14px] leading-[1.6] text-[#3A4256]"}>
          <HelpReplyText text={answer.answer} />
        </div>
      ) : (
        !answer.items.length &&
        !answer.proposal && (
          <div className={"text-[14px] text-[#7A8194]"}>
            ProCharted didn&apos;t find an answer. Try asking another way.
          </div>
        )
      )}
      {answer.chart && <AskChart chart={answer.chart} />}
      {answer.items.length > 0 && (
        <div className={"rounded-[12px] border border-[#EDEFF3] bg-white p-1"}>
          {answer.items.map((item, index) => (
            <ItemRow
              key={`${item.kind}-${item.id}-${index}`}
              item={item}
              onOpen={onOpen}
            />
          ))}
        </div>
      )}
      {answer.proposal &&
        (proposalDismissed ? (
          <div className={"text-[12.5px] font-semibold text-[#7A8194]"}>
            Cancelled. Nothing was created.
          </div>
        ) : (
          <ProposalCard
            proposal={answer.proposal}
            onReview={() => answer.proposal && onReview?.(answer.proposal)}
            onCancel={() => onDismissProposal?.()}
            onOpenLink={onOpen}
          />
        ))}
      {(answer.moreCount > 0 || answer.openLink) && (
        <div className={"flex flex-wrap items-center justify-between gap-2"}>
          <span className={"text-[12.5px] font-semibold text-[#7A8194]"}>
            {moreText(answer.moreCount)}
          </span>
          {answer.openLink && (
            <button
              type={"button"}
              onClick={() => answer.openLink && onOpen(answer.openLink.url)}
              className={
                "inline-flex h-9 items-center gap-1.5 rounded-[10px] bg-[#6B4EFF] px-3.5 text-[13px] font-bold text-white hover:bg-[#5A3EE6]"
              }>
              {answer.openLink.label}
              <LuArrowUpRight size={15} strokeWidth={2.4} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

type Turn = {
  key: number;
  question: string;
  state: State;
  /** The answer's proposal was cancelled (per turn). */
  proposalDismissed?: boolean;
};

/**
 * One conversation while the panel is open (Roadmap 1834): follow-ups go in
 * the box at the bottom and are sent with the earlier turns. Nothing is kept
 * after closing; a new question from the top search box starts over.
 */
export const AskPanel: FC<Props> = ({
  request,
  orgId,
  anchorRef,
  onClose,
  onReview,
  hidden = false,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [ask] = useAiAskMutation();
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState("");
  const turnsRef = useRef<Turn[]>([]);
  turnsRef.current = turns;
  const nextKey = useRef(0);
  // Answers that arrive after the conversation was restarted are dropped.
  const conversation = useRef(0);
  // Requests still running, by turn: Stop aborts them (Roadmap 1857). Only
  // the request registered for a turn may update it, so a stopped (or
  // repeated) request's late answer is ignored. The server still finishes
  // the stopped request (it counts as one of the 100 a day).
  const pending = useRef(new Map<number, { abort: () => void }>());
  // The answer text of each running question, shown a few words at a time
  // (the API releases a short answer in one burst; see textReveal.ts).
  const reveals = useRef(new Map<number, TextReveal>());
  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Microphone in the follow-up box (Roadmap 1852), like the top search bar.
  const dictation = useDictation((spoken) => {
    setDraft((current) => appendDictation(current, spoken));
    inputRef.current?.focus();
  });
  const position = usePanelPosition(anchorRef);

  const run = useCallback(
    (key: number, question: string, earlier: Turn[]) => {
      const id = conversation.current;
      const token: { abort: () => void } = { abort: () => undefined };
      const current = () =>
        conversation.current === id && pending.current.get(key) === token;
      const update = (state: State) => {
        if (!current()) return;
        pending.current.delete(key);
        setTurns((turns) =>
          turns.map((turn) => (turn.key === key ? { ...turn, state } : turn)),
        );
      };
      // Progress lines from the stream, shown in the "Thinking…" box.
      const progress = (text: string) => {
        if (!current()) return;
        setTurns((turns) =>
          turns.map((turn) =>
            turn.key === key
              ? { ...turn, state: withStep(turn.state, text) }
              : turn,
          ),
        );
      };
      // The answer word by word: shown in the bubble until the final answer
      // (with its items) replaces it. A burst of text is paced by TextReveal.
      reveals.current.get(key)?.stop();
      const reveal = new TextReveal((text) => {
        if (!current()) return;
        setTurns((list) =>
          list.map((turn) =>
            turn.key === key
              ? {
                  ...turn,
                  state: withStreamText(turn.state, {
                    delta: text,
                    reset: true,
                  }),
                }
              : turn,
          ),
        );
      });
      reveals.current.set(key, reveal);
      const streamed = (piece: AskTextUpdate) => {
        if (!current()) return;
        reveal.push(piece);
      };
      // The final answer waits (at most ~0.25 s) until the text shown so far
      // is complete, so the words don't jump.
      const revealed = <T,>(value: T) =>
        new Promise<T>((resolve) => {
          if (!current()) return resolve(value);
          reveal.finish(() => resolve(value));
        });
      const history = askHistory(
        earlier.map((turn) => ({
          question: turn.question,
          answer: turn.state.status === "done" ? turn.state.answer : null,
        })),
      );
      const body = {
        orgId,
        question,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...(history.length ? { history } : {}),
      };
      // Streamed (progress lines) first; if the stream can't be used (older
      // server, proxy), the same question goes to api/ai/ask as before.
      const controller = new AbortController();
      token.abort = () => controller.abort();
      pending.current.set(key, token);
      streamAsk(body, {
        signal: controller.signal,
        onStep: progress,
        onText: streamed,
      })
        .then((response) => {
          // Refresh "used today" (api/ai/ask does this through RTK Query).
          dispatch(aiSlice.util.invalidateTags(["AiStatus"]));
          return response;
        })
        .catch((error) => {
          if (!(error instanceof AskStreamUnavailable) || !current()) {
            throw error;
          }
          // Text streamed before the stream broke is dropped: the answer
          // comes from api/ai/ask.
          streamed({ delta: "", reset: true });
          const call = ask(body);
          token.abort = () => call.abort();
          return call.unwrap();
        })
        .then(revealed)
        .then((response) => {
          reveal.stop();
          update({ status: "done", answer: normalizeAskResponse(response) });
        })
        .catch((error) => {
          reveal.stop();
          update({
            status: "error",
            message: aiErrorMessage(
              error instanceof AskStreamFailed ? error.error : error,
            ),
          });
        });
    },
    [ask, dispatch, orgId],
  );

  const abortAll = useCallback(() => {
    const running = Array.from(pending.current.values());
    pending.current.clear();
    reveals.current.forEach((reveal) => reveal.stop());
    reveals.current.clear();
    running.forEach((entry) => entry.abort());
  }, []);

  // Closing the panel stops what is still running.
  useEffect(() => abortAll, [abortAll]);

  // A question from the top search box starts a new conversation.
  useEffect(() => {
    abortAll();
    conversation.current += 1;
    const key = (nextKey.current += 1);
    setTurns([
      { key, question: request.question, state: { status: "thinking" } },
    ]);
    setDraft("");
    run(key, request.question, []);
  }, [request.id, request.question, run, abortAll]);

  const busy = turns.some((turn) => turn.state.status === "thinking");

  // Stop (Roadmap 1857): the waiting question becomes "Stopped." and the box
  // is free for the next question right away.
  const stopTurns = (current: Turn[]) =>
    current.map((turn) =>
      turn.state.status === "thinking"
        ? { ...turn, state: { status: "stopped" } as State }
        : turn,
    );
  const stop = () => {
    abortAll();
    const next = stopTurns(turnsRef.current);
    turnsRef.current = next;
    setTurns(next);
    inputRef.current?.focus();
  };

  // A follow-up typed while the answer is still coming stops that question
  // first (the owner wants to correct a question without waiting).
  const askFollowUp = () => {
    const question = draft.trim();
    if (!canAsk(question)) return;
    if (busy) abortAll();
    const key = (nextKey.current += 1);
    const earlier = stopTurns(turnsRef.current);
    setTurns([...earlier, { key, question, state: { status: "thinking" } }]);
    setDraft("");
    run(key, question, earlier);
  };

  // "Ask again" repeats the newest question (same earlier turns).
  const askAgain = () => {
    const last = turnsRef.current[turnsRef.current.length - 1];
    if (!last || busy) return;
    const earlier = turnsRef.current.slice(0, -1);
    setTurns([...earlier, { ...last, state: { status: "thinking" } }]);
    run(last.key, last.question, earlier);
  };

  // The newest question scrolls to the top of the panel (answers open at
  // their start). When its answer arrives with a "Ready to create" card, the
  // panel scrolls just far enough to show the card's buttons too. The box
  // gets the focus back once it is answered.
  const lastKey = turns[turns.length - 1]?.key;
  const lastStatus = turns[turns.length - 1]?.state.status;
  useEffect(() => {
    const body = bodyRef.current;
    const last = body?.querySelector<HTMLElement>(`[data-turn="${lastKey}"]`);
    if (!body || !last) return;
    const top = Math.max(0, last.offsetTop - 12);
    if (lastStatus === "thinking") {
      body.scrollTop = top;
      return;
    }
    let target = top;
    const card = last.querySelector<HTMLElement>("[data-proposal-card]");
    if (card) {
      const cardBottom =
        card.getBoundingClientRect().bottom -
        body.getBoundingClientRect().top +
        body.scrollTop;
      // The buttons win if the question and the whole card don't fit.
      target = Math.max(top, cardBottom + 16 - body.clientHeight);
    }
    if (Math.abs(body.scrollTop - target) > 1) {
      body.scrollTo({ top: target, behavior: "smooth" });
    }
  }, [lastKey, lastStatus]);
  useEffect(() => {
    if (!busy && turns.length) inputRef.current?.focus();
  }, [busy, turns.length]);

  useEffect(() => {
    // While the review window is open, Esc belongs to that window.
    if (hidden) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [hidden, onClose]);

  const dismissProposal = (key: number) =>
    setTurns((current) =>
      current.map((turn) =>
        turn.key === key ? { ...turn, proposalDismissed: true } : turn,
      ),
    );

  const open = (link: string) => {
    onClose();
    navigate(link);
  };

  if (!position) return null;

  return createPortal(
    <div
      role={"dialog"}
      aria-label={"Ask ProCharted"}
      aria-hidden={hidden || undefined}
      style={{
        // Hidden (not unmounted) while the review window is open.
        display: hidden ? "none" : undefined,
        top: position.top,
        left: position.left,
        width: position.width,
        maxHeight: Math.min(position.maxHeight, 640),
      }}
      className={
        "fixed z-40 flex flex-col overflow-hidden rounded-[16px] border border-[#DCE2EA] bg-white font-Manrope shadow-[0_18px_48px_-12px_rgba(31,36,51,0.28)]"
      }>
      <div
        className={
          "flex items-center gap-2.5 border-b border-[#EDEFF3] px-4 py-3"
        }>
        <span
          aria-hidden={"true"}
          className={
            "flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#EEEAFF] text-[#6B4EFF]"
          }>
          <LuSparkles size={15} strokeWidth={2.2} />
        </span>
        <div
          className={
            "min-w-0 flex-1 text-[11px] font-extrabold uppercase tracking-[0.06em] text-[#6B4EFF]"
          }>
          Ask ProCharted
        </div>
        <button
          type={"button"}
          onClick={askAgain}
          disabled={busy}
          className={
            "inline-flex h-8 items-center gap-1.5 rounded-[10px] border border-[#DCE2EA] px-2.5 text-[12.5px] font-bold text-[#3A4256] hover:border-[#6B4EFF] hover:text-[#6B4EFF] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-[#DCE2EA] disabled:hover:text-[#3A4256]"
          }>
          <LuRotateCcw size={13} strokeWidth={2.4} />
          Ask again
        </button>
        <button
          type={"button"}
          aria-label={"Close"}
          onClick={onClose}
          className={
            "flex h-8 w-8 items-center justify-center rounded-[10px] text-[#7A8194] hover:bg-[#F3F4F7] hover:text-[#1F2433]"
          }>
          <LuX size={17} strokeWidth={2.2} />
        </button>
      </div>
      <div
        ref={bodyRef}
        className={"relative min-h-0 flex-1 overflow-y-auto px-4 py-4"}>
        <div className={"space-y-5"}>
          {turns.map((turn) => (
            <div key={turn.key} data-turn={turn.key} className={"space-y-3"}>
              <div className={"flex justify-end"}>
                <div
                  className={
                    "max-w-[85%] whitespace-pre-wrap break-words rounded-[14px] rounded-br-[4px] bg-[#6B4EFF] px-3.5 py-2 text-[13.5px] font-semibold leading-[1.45] text-white"
                  }>
                  {turn.question}
                </div>
              </div>
              <AnswerView
                state={turn.state}
                onStop={stop}
                onOpen={open}
                proposalDismissed={turn.proposalDismissed}
                onReview={onReview}
                onDismissProposal={() => dismissProposal(turn.key)}
              />
            </div>
          ))}
        </div>
      </div>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          askFollowUp();
        }}
        className={
          "flex items-end gap-2 border-t border-[#EDEFF3] px-3 py-2.5"
        }>
        <textarea
          ref={inputRef}
          value={draft}
          rows={1}
          maxLength={MAX_ASK_CHARS}
          aria-label={"Ask a follow-up question"}
          placeholder={"Ask a follow-up or refine the question…"}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              askFollowUp();
            }
          }}
          className={
            "max-h-28 min-h-[40px] flex-1 resize-none rounded-[10px] border border-[#DCE2EA] px-3 py-2 text-[13.5px] text-[#1F2433] outline-none placeholder:text-[#9097A8] focus:border-[#6B4EFF]"
          }
        />
        {dictation.supported && (
          <button
            type={"button"}
            aria-label={
              dictation.isListening ? "Stop dictation" : "Ask by voice"
            }
            title={
              dictation.error ||
              (dictation.isListening
                ? "Listening… press again to stop"
                : "Speak instead of typing")
            }
            onMouseDown={(event) => event.preventDefault()}
            onClick={dictation.isListening ? dictation.stop : dictation.start}
            className={classNames(
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px]",
              dictation.isListening
                ? "animate-pulse bg-[#FDECEC] text-[#E5484D]"
                : dictation.error
                ? "text-[#E5484D] hover:bg-[#FDECEC]"
                : "text-[#7A8194] hover:bg-[#F4F2FE] hover:text-[#6B4EFF]",
            )}>
            {dictation.isListening ? (
              <LuSquare size={12} fill={"currentColor"} />
            ) : (
              <LuMic size={17} strokeWidth={2.2} />
            )}
          </button>
        )}
        {busy && !canAsk(draft.trim()) ? (
          <button
            type={"button"}
            aria-label={"Stop"}
            title={"Stop this question"}
            onMouseDown={(event) => event.preventDefault()}
            onClick={stop}
            className={
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-[#1F2433] text-white hover:bg-[#3A4256]"
            }>
            <LuSquare size={12} fill={"currentColor"} />
          </button>
        ) : (
          <button
            type={"submit"}
            aria-label={busy ? "Stop and send" : "Send"}
            title={
              busy ? "Stop the current question and ask this one" : undefined
            }
            disabled={!canAsk(draft.trim())}
            className={
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-[#6B4EFF] text-white hover:bg-[#5A3EE6] disabled:cursor-not-allowed disabled:opacity-40"
            }>
            <LuArrowUp size={17} strokeWidth={2.5} />
          </button>
        )}
      </form>
      <div
        className={
          "border-t border-[#EDEFF3] bg-[#FAFBFC] px-4 py-2 text-[11.5px] font-medium text-[#8A90A2]"
        }>
        AI answer from your firm&apos;s data. It can make mistakes.
      </div>
    </div>,
    document.body,
  );
};
